import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { STALE } from '@/lib/query-cache.js';
import { LIVE_VIEW } from './useIssues.js';
import type { FollowStatus, JiraStatus, JiraUserRef, JiraIssueLink } from '@shared/types.js';

export function useFollowStatus(issueKey: string) {
  return useQuery({
    queryKey: ['follow', issueKey],
    queryFn: () => api.get<FollowStatus>(`/api/issues/${issueKey}/follow`).then((r) => r.data),
    enabled: !!issueKey,
    staleTime: STALE.MODERATE,  // 10 min — follows don't change often
  });
}

export function useFollowMutation(issueKey: string) {
  const queryClient = useQueryClient();

  const follow = useMutation({
    mutationFn: () => api.post<FollowStatus>(`/api/issues/${issueKey}/follow`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['follow', issueKey] });
      const prev = queryClient.getQueryData<FollowStatus>(['follow', issueKey]);
      queryClient.setQueryData<FollowStatus>(['follow', issueKey], { following: true, issueKey });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['follow', issueKey], context.prev);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['follow', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['follows'] });
    },
  });

  const unfollow = useMutation({
    mutationFn: () => api.delete<FollowStatus>(`/api/issues/${issueKey}/follow`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['follow', issueKey] });
      const prev = queryClient.getQueryData<FollowStatus>(['follow', issueKey]);
      queryClient.setQueryData<FollowStatus>(['follow', issueKey], { following: false, issueKey });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['follow', issueKey], context.prev);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['follow', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['follows'] });
    },
  });

  return { follow, unfollow };
}

export interface FollowedIssueSummary {
  issueKey: string;
  summary: string;
  status: string;
  statusCategory?: JiraStatus['category'];
  priority: string;
  priorityIconUrl?: string | null;
  issuetype?: string;
  assignee?: JiraUserRef | null;
  updated: string;
  links?: JiraIssueLink[];
}

export function useFollowedIssues() {
  return useQuery({
    queryKey: ['follows'],
    queryFn: () => api.get<FollowedIssueSummary[]>('/api/follows').then((r) => r.data),
    ...LIVE_VIEW,  // the tickets' status and assignee change elsewhere
  });
}
